import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "../../hooks/useAuth0";
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Divider,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import ScienceIcon from "@mui/icons-material/Science";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import api from '../../services/api';

const TrialMatchingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loginWithRedirect } =
    useAuth0();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [patientData, setPatientData] = useState(null);
  const [matchedTrials, setMatchedTrials] = useState([]);
  const [totalMatches, setTotalMatches] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) {
      loginWithRedirect();
      return;
    }
    loadMatchedTrials();
  }, [isAuthenticated]);

const loadMatchedTrials = async () => {
  try {
    setLoading(true);
    setError(null);

    const response = await api.get('/v1/patient-matching/my-matches');
    const data = response.data;

    console.log("✅ Matched trials response:", data);

    setPatientData({
      id: data.patientInfo.patientId,
      name: `${data.patientInfo.firstName} ${data.patientInfo.lastName}`,
      ckdStage: data.patientInfo.ckdStage || "Not specified",
      firstName: data.patientInfo.firstName,
      lastName: data.patientInfo.lastName,
    });

    setMatchedTrials(data.matchedTrials || []);
    setTotalMatches(data.totalMatches || 0);

  } catch (err) {
    console.error("❌ Error loading matched trials:", err);
    if (err.response?.status === 404) {
      setError("Patient profile not found. Please create your profile first.");
      setTimeout(() => navigate("/patients/new"), 3000);
    } else if (err.response?.status === 401) {
      setError("Authentication failed. Please log in again.");
      setTimeout(() => loginWithRedirect(), 2000);
    } else {
      setError(err.response?.data?.message || "Failed to load matched trials");
    }
  } finally {
    setLoading(false);
  }
};

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || "";
    if (statusLower.includes("recruiting")) return "success";
    if (statusLower.includes("active")) return "info";
    if (statusLower.includes("completed")) return "default";
    if (statusLower.includes("terminated")) return "error";
    return "default";
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 3 }}>
            Loading matched trials...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/patients/new")}
          >
            Create Patient Profile
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 4,
          background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
          color: "white",
        }}
      >
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Clinical Trial Matching
        </Typography>
        <Typography variant="subtitle1">
          Personalized clinical trial recommendations based on your profile
        </Typography>
      </Paper>

      {/* Patient Info Card */}
      {patientData && (
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <PersonIcon color="primary" sx={{ fontSize: 40 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight="bold">
                {patientData.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Patient ID: {patientData.id}
              </Typography>
            </Box>
            {/* ✅ ADDED - Edit Profile Button */}
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/patients/edit/${patientData.id}`)}
            >
              Edit Profile
            </Button>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>CKD Stage:</strong> {patientData.ckdStage}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Matched Trials:</strong> {totalMatches}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Results Summary */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          {totalMatches === 0
            ? "No Matching Trials Found"
            : `${totalMatches} Matching Trial${totalMatches !== 1 ? "s" : ""
            } Found`}
        </Typography>
        <Button variant="outlined" onClick={loadMatchedTrials}>
          Refresh Results
        </Button>
      </Box>

      {/* No Results */}
      {matchedTrials.length === 0 ? (
        <Paper elevation={1} sx={{ p: 6, textAlign: "center" }}>
          <ScienceIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Matching Trials Available
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            We couldn't find any clinical trials matching your profile at this
            time. New trials are added regularly, so please check back soon.
          </Typography>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => {
              // ✅ FIXED - Use correct patient ID
              if (patientData?.id) {
                navigate(`/patients/edit/${patientData.id}`);
              } else {
                navigate("/patients/new");
              }
            }}
          >
            Update My Profile
          </Button>
        </Paper>
      ) : (
        // Trial Cards
        <Grid container spacing={3}>
          {matchedTrials.map((match, index) => (
            <Grid item xs={12} key={index}>
              <Card
                elevation={2}
                sx={{
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {match.trial.title || "Untitled Trial"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        NCT ID: {match.trial.nctId}
                      </Typography>
                    </Box>
                    <Chip
                      label={match.trial.status || "Status Unknown"}
                      color={getStatusColor(match.trial.status)}
                      sx={{ ml: 2 }}
                    />
                  </Box>

                  {/* Match Score */}
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={`Match Score: ${match.matchScore}%`}
                      color={
                        match.matchScore >= 80
                          ? "success"
                          : match.matchScore >= 60
                            ? "warning"
                            : "default"
                      }
                      size="small"
                    />
                  </Box>

                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <ScienceIcon color="action" fontSize="small" />
                        <Typography variant="body2">
                          <strong>Type:</strong>{" "}
                          {match.trial.ckdStage || "Not specified"}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <LocationOnIcon color="action" fontSize="small" />
                        <Typography variant="body2">
                          <strong>Phase:</strong>{" "}
                          {match.trial.phase || "Not specified"}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <CalendarTodayIcon color="action" fontSize="small" />
                        <Typography variant="body2">
                          <strong>Locations:</strong>{" "}
                          {match.trial.locations || "Not specified"}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {match.trial.briefSummary && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {match.trial.briefSummary.length > 300
                          ? match.trial.briefSummary.substring(0, 300) + "..."
                          : match.trial.briefSummary}
                      </Typography>
                    </Box>
                  )}

                  {match.trial.sponsor && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>Sponsor:</strong> {match.trial.sponsor}
                      </Typography>
                    </Box>
                  )}

                  {/* Match Reasons */}
                  {match.matchReasons && match.matchReasons.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        gutterBottom
                      >
                        Why this trial matches:
                      </Typography>
                      {match.matchReasons.map((reason, i) => (
                        <Chip
                          key={i}
                          label={reason}
                          size="small"
                          sx={{ mr: 1, mb: 1 }}
                        />
                      ))}
                    </Box>
                  )}
                </CardContent>

                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Button
                        variant="outlined"
                        startIcon={<OpenInNewIcon />}
                        onClick={() => navigate(`/trials/${match.trial.nctId}`)}
                        fullWidth
                      >
                        View Details
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Button
                        variant="contained"
                        onClick={() => {
                          // Navigate to travel booking with trial data
                          navigate('/travel/book', {
                            state: {
                              trial: {
                                nctId: match.trial.nctId || '',
                                officialTitle: match.trial.officialTitle || match.trial.title || '',
                                facility: '',
                                city: '',
                                state: '',
                                address: '',
                              }
                            }
                          });
                        }}
                        fullWidth
                        sx={{
                          bgcolor: '#1976d2',
                          '&:hover': { bgcolor: '#1565c0' }
                        }}
                      >
                        Book Travel
                      </Button>
                    </Grid>
                  </Grid>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default TrialMatchingPage;